import classNames from 'classnames';
import { format } from 'date-fns';
import { camelCase } from 'lodash';

import { AppValidation, DeserializedAppData } from '@api';

import { AddDate } from '@uikit/icons/AddDate';

import NextLink from '@shared/common/components/NextLink';
import {
  APP_STATUS,
  AppStatusValue,
  getAppStatusConfig,
  getAppStatusTextColorClassName,
} from '@shared/helpers/app';

import I18nInfoTooltip from '@store:web/components/common/I18nInfoTooltip/I18nInfoTooltip';

interface AppDetailsStatusProps {
  app?: DeserializedAppData;
  status?: AppStatusValue | null;
  className?: string;
  validation?: AppValidation;
}

const AppDetailsStatus = ({ className, app, validation, status }: AppDetailsStatusProps) => {
  const config = getAppStatusConfig(status);

  if (!config || !status) {
    return null;
  }

  const { Icon, label } = config;
  const lowerCasedStatus = camelCase(status);
  const isReadOnly = status === APP_STATUS.READ_ONLY;

  return (
    <span
      className={classNames(
        't-body4 inline-flex items-center gap-1',
        getAppStatusTextColorClassName(status),
        className,
      )}
      data-qa="info-status"
    >
      <span className="icon-2xs">
        <Icon />
      </span>
      {label}
      {!isReadOnly && (
        <I18nInfoTooltip
          iconClassName="text-neutral-200"
          title={`appPage_statusIcon_${lowerCasedStatus}_title`}
          tooltipClassName="!w-auto !max-w-[25.625rem]"
          content={({ t }) => {
            return t.rich(`appPage_statusIcon_${lowerCasedStatus}_description`, {
              'validated-date': () => {
                if (!validation?.endDate) {
                  return null;
                }

                return (
                  <div className="flex justify-between items-end text-sm mt-2.5">
                    <div className="flex gap-2 flex-wrap font-medium">
                      <span className="flex gap-2 items-center">
                        <AddDate className="icon-xs text-neutral-600" />
                        <span className="text-neutral-300">Validated date:</span>
                      </span>
                      <span className="text-neutral-500">
                        {format(new Date(validation.endDate), 'dd MMMM yyyy')}
                      </span>
                    </div>

                    <NextLink
                      href={`/app/${app?.attributes.appLink}/validation`}
                      className="text-sm underline text-primary font-light"
                    >
                      Learn More
                    </NextLink>
                  </div>
                );
              },
            });
          }}
        />
      )}
    </span>
  );
};

export default AppDetailsStatus;
